import { ArrowPathIcon, CheckCircleIcon } from '@heroicons/react/24/solid'
import { useHistory } from '../../../providers/HistoryProvider'
import { Modal } from '../../common/Modal'
import RegenerateNotesMain from './modalpages/RegenerateNotesMain'
import { useEffect, useMemo, useState } from 'react'
import { SelectInputsForNotes } from './modalpages/SelectInputsForNotes'

enum RegenerateNotesPage {
    MAIN = "MAIN",
    SELECT_RECORDINGS = "SELECT_RECORDINGS",
}

export default function RegenerateNotesModal() {
    const { 
        template,
        notesReprocessVisible, 
        handleNotesReprocessVisible, 
        selectedNoteMetadata,
        reprocessNotesWithSelected,
    } = useHistory()

    const [step, setStep] = useState<RegenerateNotesPage>(RegenerateNotesPage.MAIN)

    const [selectedRecordings, setSelectedRecordings] = useState<string[]>([]);
    const [lastSavedSelectedRecordings, setLastSavedSelectedRecordings] = useState<string[]>([]);
    const [shouldUseSelectedRecordings, setShouldUseSelectedRecordings] = useState<boolean>(false);
    
    useEffect(() => {
        if (notesReprocessVisible && selectedNoteMetadata) {
            setSelectedRecordings([...selectedNoteMetadata.depends_on_ids])
            setLastSavedSelectedRecordings([...selectedNoteMetadata.depends_on_ids]); // Save the initial state for back navigation
        } else {
            setSelectedRecordings([]);
            setLastSavedSelectedRecordings([]);
        }
    }, [notesReprocessVisible, selectedNoteMetadata]);

    function onClose() {
        handleNotesReprocessVisible(false)
        setStep(RegenerateNotesPage.MAIN)
        setSelectedRecordings([])
        setLastSavedSelectedRecordings([])
        setShouldUseSelectedRecordings(false)
    }

    async function handleRegenerateNotes(additionalInstructions?: string) {
        onClose();
        await reprocessNotesWithSelected(
            shouldUseSelectedRecordings ? lastSavedSelectedRecordings : undefined, 
            additionalInstructions
        )        
    }

    function handleBackFromSelection() {
        setSelectedRecordings([...lastSavedSelectedRecordings]);
        setStep(RegenerateNotesPage.MAIN)
    }

    function handleContinueWithSelection() {
        setLastSavedSelectedRecordings([...selectedRecordings]);
        setStep(RegenerateNotesPage.MAIN)
        setShouldUseSelectedRecordings(true);
        return Promise.resolve(); // Return a resolved promise to avoid async issues
    }

    const maxWidth = useMemo(() => {
        return step === RegenerateNotesPage.SELECT_RECORDINGS ? "sm:max-w-3xl" : "sm:max-w-lg"
    }, [step])
    
    return (
        <Modal
            isOpen={notesReprocessVisible}
            onClose={onClose}
            maxWidth={maxWidth}
            title={`Regenerate note`}
            subTitle='Reprocess your notes with a different template.'
            icon={<ArrowPathIcon className="h-6 w-6 text-blue-600" aria-hidden="true" />}
        >
            {step === RegenerateNotesPage.MAIN && <RegenerateNotesMain 
                onReselectRecordings={() => setStep(RegenerateNotesPage.SELECT_RECORDINGS)} 
                handleGenerate={handleRegenerateNotes}
                recordingsToUse={shouldUseSelectedRecordings ? lastSavedSelectedRecordings : undefined}
            />}
            {step === RegenerateNotesPage.SELECT_RECORDINGS && <SelectInputsForNotes 
                onBack={handleBackFromSelection}
                template={template}
                selectedRecordings={selectedRecordings}
                setSelectedRecordings={setSelectedRecordings}
                primaryButtonText="Continue with selection"
                handlePrimaryButtonClick={handleContinueWithSelection}
                primaryButtonIcon={<CheckCircleIcon className="h-5 w-5" aria-hidden="true" />}
            />}
        </Modal>
    )
}